import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { FaCheck as CheckIcon } from "react-icons/fa";
import { MdContentCopy as CopyIcon } from "react-icons/md";
import styles from "./TextCopy.module.scss";

export interface TextCopyProps {
  text: string;
  ariaLabel?: string;
  className?: string;
  maxLength?: number;
  iconOnlyFeedback?: boolean;
}

export const TextCopy = ({ text, ariaLabel, className = "", maxLength = 60, iconOnlyFeedback = false }: TextCopyProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fixedWidth, setFixedWidth] = useState<string>("auto");
  const label = text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;

  useEffect(() => {
    if (textRef.current) setFixedWidth(`${textRef.current.offsetWidth}px`);
  }, [label]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1000);
    } catch {
      setCopied(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    void handleCopy();
  };

  return (
    <div
      className={`${styles.root} ${className}`}
      onClick={() => void handleCopy()}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel || `Copy ${text}`}
    >
      <div ref={textRef} className={styles.hiddenText} aria-hidden="true">
        <span>{label}</span>
        <span className={styles.icon}><CopyIcon /></span>
      </div>
      <div className={styles.text} style={{ width: fixedWidth }} aria-live="polite">
        {iconOnlyFeedback ? (
          <><span className={styles.label}>{label}</span><span className={styles.icon}>{copied ? <CheckIcon /> : <CopyIcon />}</span></>
        ) : copied ? (
          <>Copied <span className={styles.icon}><CheckIcon /></span></>
        ) : (
          <><span className={styles.label}>{label}</span><span className={styles.icon}><CopyIcon /></span></>
        )}
      </div>
    </div>
  );
};

export default TextCopy;
